import useAppSelector from 'hooks/useAppSelector';
import useAppDispatch from 'hooks/useAppDispatch';

import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Redux selectors and actions
import {
    clearErrors,
    resetOrderState,
    clearSearchOrders,
    orderSelectors,
    orderThunks,
} from 'store/reducers/orderSlice';
import { selectOrigin } from 'store/reducers/navigationSlice';
import {
    ExtendedReturnItem,
    ReturnItemProperty,
    searchItemsStringSet,
    returnSelectors,
    returnThunks,
    updateReturnItem,
    setAllReturnItemsToMaxQty,
    incrementReturnItemQtyByScan,
    setArrivalDate,
    removeOrderFromReturnSlip,
    clearReturnSlip as clearReturnSlipAction,
} from 'store/reducers/returnSlice';
import { configSelectors, configThunks } from 'store/reducers/configSlice';
import { decisionCodeSelectors } from 'store/reducers/decisionCodeSlice';
import { userSelector, userThunks } from 'store/reducers/usersSlice';
import {
    selectBatchDestinations,
    selectOpenBatchReturns,
    fetchBatchDestinations,
    fetchOpenBatchReturns,
} from 'store/reducers/batchReturnSlice';

import {
    faExclamationTriangle,
    faPlus,
    faAngleUp,
    faBarcode,
    faMagnifyingGlass,
    faBoxesStacked,
    faCheck,
} from '@fortawesome/pro-light-svg-icons';

// Helpers
import { canIncrementByScan, getReturnItemId, getStatsTranslations, scanMatchesLine } from 'utils/helpers';

// Custom hooks
import useBreakPoints from 'hooks/useBreakPoints';
import useKeyboardNavigation from 'hooks/useKeyboardNavigation';
import useReturnSlipActions from 'hooks/useReturnSlipActions';
import { useOutletContext } from 'react-router-dom';
import useSplitRowManagement from 'hooks/useSplitRowManagement';
import useOrderTableRows from 'hooks/useOrderTableRows';
import useGoHome from 'hooks/useGoHome';
import useGoBackNavigation from 'hooks/useGoBackNavigation';
import useNavigationGuard from 'hooks/useNavigationGuard';

// Types
import type { LayoutOutletContext } from 'components/Returns/Layout';
import { ReturnOrderLineType, ReturnStatusType, ReturnOrderLine } from 'returnCenter-api';

// Components
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled, { keyframes } from 'styled-components';
import Grid from 'components/ui/Grid';
import { toast } from 'react-toastify';
import SecondaryActionButton from 'components/OrderActions/AddOrder';
import Text from 'components/ui/Text';
import ReturnActionPanel from 'components/OrderActions/ReturnActionPanel';
import Flex from 'components/ui/Flex';
import Table from 'components/ui/Table';
import SideBar from 'components/SideBar/SideBar';
import CommentsDrawer, { CommentsDrawerTarget } from 'components/ReturnStatus/CommentsDrawer';
import BatchReturnDrawer, { BatchDrawerTarget } from 'components/Orders/BatchReturnDrawer';
import BatchReturnModal from 'components/Orders/BatchReturnModal';
import { canMoveBatchRow } from 'utils/batchRowMove';
import ReturnLogs from 'components/ReturnLogs/ReturnLogs';
import OrderInfoCard from 'components/CustomerDetailsCard/OrderInfoCard';
import { CreditNotesCard } from 'components/CreditNotes';
import B2CPanel from 'components/B2C/B2CPanel';
import BatchSourceCard from 'components/batch-returns/BatchSourceCard';
import { ConnectedReturnsCard } from 'components/ConnectedReturns';
import OrderHeadDetails from './OrderHead';
import OrderDialogs from './OrderDialogs';
import ReturnAdditionalCosts from 'components/ReturnAdditionalCosts/ReturnAdditionalCosts';
import IntercompanyPanel from 'components/Intercompany/IntercompanyPanel';
import ErrorDisplay from 'components/Error/ErrorDisplay';
import AdditionalOrderEditor from 'components/OrderActions/AdditionalOrderEditor';
import SearchInput from 'components/Search/SearchInput';
import type { SearchMode as AdditionalOrderSearchMode } from 'components/Search/SearchModal';
import OrderItemCountDisplay from 'components/Orders/OrderItemCountDisplay';
import NoResultsFound from 'components/ui/NoResultsFound';
import Spinner from 'components/ui/Spinner';
import InvoiceChargesTable from 'components/InvoiceChargesTable/InvoiceChargesTable';
import { LIGHT_BLUE } from 'styles/globalstyles';
import { companiesSelector } from 'store/reducers/companiesSlice';
import useAutoApproveBatchHandling from 'hooks/useAutoApproveBatchHandling';

const SCAN_MODE = 'scan' as const;
const MANUAL_MODE = 'manual' as const;
export const MULTI_MATCH = 'multi-match' as const;
export const ACTIVE_MULTI_MATCH = 'active-multi-match' as const;
export const UNIQUE = 'unique' as const;

export type HandleReturnItemChange = (
    orderNumber: string,
    sku: string,
    orderLineNumber: string,
    itemId: number | string,
    property: ReturnItemProperty,
    value: number | null | boolean | string,
    sourceReturnId?: number | null,
) => void;

const Order = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const bp = useBreakPoints();
    const location = useLocation();
    const { state } = location;
    const { orderIdFromPath, returnIdFromPath, returnStatus, fillMaxReturnQty } = state || {};
    const origin = useAppSelector(selectOrigin);
    const url = useParams();
    const isSearchOrderRoute = location.pathname.startsWith('/search/order/');

    // Refs
    const searchItemContainerRef = useRef<HTMLDivElement>(null);
    const searchItemRef = useRef<HTMLInputElement>(null);
    const orderListRef = useRef<HTMLDivElement>(null);
    const rowRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
    const rowInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

    // Redux selectors
    const errorCode = useAppSelector(orderSelectors.selectErrorCode);
    const searchQuery = useAppSelector(returnSelectors.selectSearchItemsString);
    const orderError = useAppSelector(orderSelectors.selectOrderErrorOnLoading);
    const returnSlipError = useAppSelector(returnSelectors.selectReturnSlipErrorOnLoading);
    const errorMessage = useAppSelector(returnSelectors.selectErrorMessage);
    const returnSlip = useAppSelector(returnSelectors.selectReturnSlip);
    const returnSlipStatus = useAppSelector(returnSelectors.selectReturnSlipStatus);
    const returnSlipId = useAppSelector(returnSelectors.selectReturnId);
    const orderLoading = useAppSelector(orderSelectors.selectOrderLoading);
    const returnSlipLoading = useAppSelector(returnSelectors.selectReturnSlipLoading);
    const orderNumbers = useAppSelector(returnSelectors.selectSelectedOrderNumbers);
    const filteredReturnItems = useAppSelector(returnSelectors.selectFilteredReturnItems);
    const hasUnsavedChanges = useAppSelector(returnSelectors.selectHasUnsavedChanges);
    const currentCompany = useAppSelector(companiesSelector.selectCurrentCompany);

    const {
        dontAskAgainBulkAction,
        dontAskAgainCompleteReturn,
        dontAskAgainDeleteReturn,
        dontAskAgainBulkMaxQty,
    } = useAppSelector(userSelector.selectDialogPreferences);
    const decisionCodes = useAppSelector(decisionCodeSelectors.selectDecisionCodes);
    const buttonStates = useAppSelector(returnSelectors.selectReturnButtonStates);
    const hasSplitRows = useAppSelector(returnSelectors.selectHasSplitRows);
    const settings = useAppSelector(configSelectors.selectSettings);
    const isM3Company = useAppSelector(configSelectors.selectIsM3Company);
    const completesWithoutCredit = useAppSelector(returnSelectors.selectCompletesWithoutCredit);
    const settingsLoading = useAppSelector(configSelectors.selectSettingsLoading);
    const settingsAvailable = useAppSelector(configSelectors.selectSettingsAvailable);
    const isRequestedReturn = useAppSelector(returnSelectors.selectIsRequestedReturn);
    const misPickReasonCodeIdSet = useAppSelector(configSelectors.selectMisPickReasonCodeIds);
    const misPickReasonCodeIds = useMemo(() => Array.from(misPickReasonCodeIdSet), [misPickReasonCodeIdSet]);

    // Derived state - single source of truth for loading/error states
    const hasError = orderError || returnSlipError;
    const isLoading = orderLoading || returnSlipLoading;
    const hasDecisionCodes = !!(decisionCodes && decisionCodes.length > 0);

    // Local state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [pendingReasonCode, setPendingReasonCode] = useState<number | null>(null);
    const [bulkActionCheckbox, setBulkActionCheckbox] = useState(false);
    const [showCompleteDialog, setShowCompleteDialog] = useState(false);
    const [completeReturnCheckbox, setCompleteReturnCheckbox] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [deleteReturnCheckbox, setDeleteReturnCheckbox] = useState(false);
    const [dialogOpenedByScanner, setDialogOpenedByScanner] = useState(false);
    const [pendingProcessingStatus, setPendingProcessingStatus] = useState<ReturnStatusType | null>(null);
    const [showBulkMaxQtyDialog, setShowBulkMaxQtyDialog] = useState(false);
    const [bulkMaxQtyCheckbox, setBulkMaxQtyCheckbox] = useState(false);
    const [showCompleteBatchDialog, setShowCompleteBatchDialog] = useState(false);
    const [searchMode, setSearchMode] = useState<typeof SCAN_MODE | typeof MANUAL_MODE>(SCAN_MODE);
    const [additionalOrderSearchMode, setAdditionalOrderSearchMode] = useState<AdditionalOrderSearchMode>(
        'orderNumber',
    );
    const [pendingRemoveOrderNumber, setPendingRemoveOrderNumber] = useState<string | null>(null);
    const [activeHighlightedRowKey, setActiveHighlightedRowKey] = useState<string | null>(null);
    const [commentsTarget, setCommentsTarget] = useState<CommentsDrawerTarget | null>(null);
    const [batchDrawerTarget, setBatchDrawerTarget] = useState<BatchDrawerTarget | null>(null);
    const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);

    // Batch return selectors
    const batchDestinations = useAppSelector(selectBatchDestinations);
    const openBatchReturns = useAppSelector(selectOpenBatchReturns);

    // Computed values
    const compact = bp.width < 900;
    const isBlocked = returnSlipStatus === ReturnStatusType.OnHold;
    const isCreditedBatchPending = returnSlipStatus === ReturnStatusType.CreditedBatchPending;
    const isCompletedOrHasError =
        returnSlipStatus === ReturnStatusType.Completed ||
        returnSlipStatus === ReturnStatusType.CreditError ||
        returnSlipStatus === ReturnStatusType.BatchHandled ||
        isCreditedBatchPending;
    const showLpn = !settingsLoading && isM3Company;
    // A return in Processing status may have an ERP credit operation in flight. Any mutation
    // (status change, complete, delete) must first warn the user to verify the ERP.
    const isProcessing = returnSlipStatus === ReturnStatusType.Processing;
    const statusButtonLabelWhenBlocked = isBlocked ? t('setToInProgress') : t('pauseReturn');
    const isNewReturn = returnSlipId === null;
    const isScanIncrementMode =
        returnSlipStatus !== ReturnStatusType.Completed &&
        returnSlipStatus !== ReturnStatusType.CreditError &&
        returnSlipStatus !== ReturnStatusType.CreditedBatchPending &&
        returnSlipStatus !== ReturnStatusType.BatchHandled;

    // Once a batch is sent the slip is locked, but rows the receiver has not started on can still
    // move to another batch. Such changes are moves, made straight away instead of saved with the slip.
    const batchMoveMode = (returnSlip?.isLockedByBatch ?? false) || (isCompletedOrHasError && !isCreditedBatchPending);
    const { hasBatchReturnItems, hasMovableBatchRows } = useMemo(() => {
        const items = (returnSlip?.returnItems ?? []).flatMap((group) =>
            (group.orderLines ?? []).flatMap((ol) => ol.returnItems ?? []),
        );
        return {
            hasBatchReturnItems: items.some((ri) => ri.batchReturnDestinationId != null),
            hasMovableBatchRows: batchMoveMode && items.some(canMoveBatchRow),
        };
    }, [returnSlip, batchMoveMode]);

    const batchDestinationNames = useMemo(
        () =>
            batchDestinations.reduce((acc, d) => {
                if (d.id != null && d.name != null) acc[d.id] = d.name;
                return acc;
            }, {} as Record<number, string>),
        [batchDestinations],
    );

    // Navigation guard — shows confirmation dialog when navigating away with unsaved changes
    const { isBlocked: isNavBlocked, proceed: proceedNavigation, cancel: cancelNavigation } = useNavigationGuard(
        hasUnsavedChanges && !isCompletedOrHasError,
    );

    // Custom hooks
    const { cellInputRefs, handleArrowKeyDown } = useKeyboardNavigation({ returnSlip });

    // Use Outlet context provided by Layout for scan highlights and to register handlers
    const outlet = useOutletContext<LayoutOutletContext | undefined>();
    const highlightedKey = outlet?.highlightedKey ?? null;
    const clearHighlight = outlet?.clearHighlight ?? (() => {});

    const blurActiveElement = useCallback(() => {
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
    }, []);

    // Handler for scan increment mode - increments qty of scanned item
    const handleScanIncrement = useCallback(
        (scanKey: string) => {
            const result = canIncrementByScan(returnSlip, scanKey);

            if (result.canIncrement) {
                dispatch(incrementReturnItemQtyByScan({ scanKey }));

                // Scroll to the exact row being incremented
                const lowerKey = scanKey.toLowerCase();
                for (const orderGroup of returnSlip?.returnItems ?? []) {
                    for (const orderLine of orderGroup.orderLines ?? []) {
                        if (!scanMatchesLine(orderLine, orderGroup.orderLines, lowerKey)) continue;
                        const alreadyReturnedQty = (orderLine.returnItems ?? []).reduce(
                            (sum, item) => sum + (item.quantityInReturn ?? 0),
                            0,
                        );
                        if (alreadyReturnedQty < (orderLine.quantityAvailableForReturn ?? 0)) {
                            const key = `${orderGroup.orderNumber}-${orderLine.ean ?? orderLine.sku}`;
                            setActiveHighlightedRowKey(key);
                            rowRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'center' });

                            const targetItem = orderLine.returnItems?.find(
                                (ri) => (ri.quantityInReturn ?? 0) < (orderLine.quantityAvailableForReturn ?? 0),
                            );
                            if (targetItem) {
                                const itemId = getReturnItemId(targetItem);

                                if (outlet?.pendingSingleItemRef) {
                                    outlet.pendingSingleItemRef.current = {
                                        orderNumber: orderGroup.orderNumber ?? '',
                                        sku: orderLine.sku ?? '',
                                        orderLineNumber: orderLine.orderLineNumber ?? '',
                                        itemId: itemId ? itemId.toString() : '',
                                    };
                                }
                            }
                            return;
                        }
                    }
                }
            } else if (result.sku) {
                toast.error(t('cannotReturnMoreThanShipped', { max: result.max, sku: result.sku }), {
                    autoClose: 3000,
                    closeOnClick: true,
                });
            } else {
                // No match found at all — EAN does not exist in this return
                toast.error(t('eanNotFoundInReturn'), {
                    autoClose: 3000,
                    closeOnClick: true,
                    icon: <FontAwesomeIcon icon={faExclamationTriangle} />,
                });
            }
        },
        [dispatch, returnSlip, t, rowRefs, outlet],
    );

    const highlightVariant = useMemo(() => {
        if (!highlightedKey || !returnSlip?.returnItems) return null;
        const searchPhrase = highlightedKey.toLowerCase();
        let count = 0;
        for (const groupedOrders of returnSlip.returnItems) {
            for (const orderLine of groupedOrders.orderLines ?? []) {
                if (scanMatchesLine(orderLine, groupedOrders.orderLines, searchPhrase)) {
                    count++;
                    if (count > 1) return MULTI_MATCH;
                }
            }
        }
        return count === 1 ? UNIQUE : null;
    }, [highlightedKey, returnSlip]);

    // Clear the active-row highlight when the global scan highlight is cleared
    useEffect(() => {
        if (!highlightedKey) {
            setActiveHighlightedRowKey(null);
        }
    }, [highlightedKey]);
    /* --------- end of barcode scanning hook integration --------- */

    // Custom hooks for actions
    const { handleAddSplitItem, handleRemoveSplit } = useSplitRowManagement({ cellInputRefs });

    const {
        // handleSaveReturnSlip,
        handleSaveWithStatus,
        handleCompleteReturnSlip,
        handleCompleteBatch,
        handleReopenBatchSlip,
        handleApproveBatchHandling,
        handleUnapproveBatchHandling,
        getUnassignedBatchItemCount,
    } = useReturnSlipActions({
        returnSlip,
        returnSlipId,
        orderIdFromPath,
        returnStatus,
    });

    // Approves batch handling once every row that can go into a batch has one.
    useAutoApproveBatchHandling();

    // Handler for complete return button
    const handleCompleteReturnClick = useCallback(() => {
        if (dontAskAgainCompleteReturn) {
            handleCompleteReturnSlip();
        } else {
            setShowCompleteDialog(true);
        }
    }, [dontAskAgainCompleteReturn, handleCompleteReturnSlip]);

    // Handler for confirming in dialog
    const handleConfirmCompleteReturn = useCallback(() => {
        handleCompleteReturnSlip();
        if (completeReturnCheckbox) {
            dispatch(userThunks.setDialogPreference({ key: 'dontAskAgainCompleteReturn', value: true }));
        }
        setShowCompleteDialog(false);
        setCompleteReturnCheckbox(false);
        clearHighlight();
    }, [handleCompleteReturnSlip, completeReturnCheckbox, dispatch, clearHighlight]);

    // Handler for canceling dialog
    const handleCancelCompleteReturn = useCallback(() => {
        setShowCompleteDialog(false);
        setCompleteReturnCheckbox(false);
    }, []);

    // Handler for complete batch return click
    const handleCompleteBatchClick = useCallback(() => {
        const unassignedCount = getUnassignedBatchItemCount();
        if (unassignedCount > 0) {
            setShowCompleteBatchDialog(true);
        } else {
            handleCompleteBatch();
        }
    }, [getUnassignedBatchItemCount, handleCompleteBatch]);

    const handleConfirmCompleteBatch = useCallback(() => {
        handleCompleteBatch();
        setShowCompleteBatchDialog(false);
    }, [handleCompleteBatch]);

    const handleCancelCompleteBatch = useCallback(() => {
        setShowCompleteBatchDialog(false);
    }, []);

    // Handle return item change
    const handleReturnItemChange = useCallback(
        (
            orderNumber: string,
            sku: string,
            orderLineNumber: string,
            itemId: number | string,
            property: ReturnItemProperty,
            value: number | null | boolean | string,
            sourceReturnId?: number | null,
        ) => {
            dispatch(
                updateReturnItem({
                    orderNumber,
                    sku,
                    orderLineNumber,
                    itemId,
                    property,
                    value,
                    sourceReturnId,
                    misPickReasonCodeIds,
                }),
            );
        },
        [dispatch, misPickReasonCodeIds],
    );

    // Table rows hook
    const { getRows } = useOrderTableRows({
        returnSlip,
        searchQuery,
        isCompletedOrHasError,
        batchMoveMode,
        hasDecisionCodes,
        hideCredit: completesWithoutCredit,
        hasBatchReturnItems,
        isRequestedReturn,
        showLpn,
        origin,
        handleReturnItemChange,
        handleAddSplitItem,
        handleRemoveSplit,
        handleArrowKeyDown,
        onOpenComments: (orderNumber, orderline, returnItem) =>
            setCommentsTarget({ orderNumber, orderline, returnItem }),
        onOpenBatchDrawer: (orderNumber, orderline, returnItem) =>
            setBatchDrawerTarget({ orderNumber, orderline, returnItem }),
        batchDestinationNames,
        rowRefs,
        rowInputRefs,
        cellInputRefs,
    });

    // Table columns
    const tableColumns = [
        '#',
        t('productInformation'),
        t('location'),
        t('shippedQty'),
        t('returnQty'),
        ...(isRequestedReturn ? [t('quantityRequested'), t('approved')] : []),
        t('reason'),
        ...(showLpn ? [t('lpn', 'LPN')] : []),
        ...(!completesWithoutCredit ? [t('refund')] : []),
        ...(!hasDecisionCodes && !completesWithoutCredit ? [t('returnToStock')] : []),
        ...(hasBatchReturnItems ? [t('batchReturn.column', 'Batch')] : []),
        t('notes'),
        t('returnsOnThisLine.column', 'Returns on this line'),
        '',
    ];

    // Clear errors on unmount
    useEffect(() => {
        return () => {
            dispatch(clearErrors());
        };
    }, [dispatch]);

    // Load data based on route params
    useEffect(() => {
        if (orderIdFromPath || (isSearchOrderRoute && url.id)) {
            const orderId = orderIdFromPath || url.id;
            dispatch(
                orderThunks.getOrderAndAddToReturnSlip({
                    orderId,
                    isAddingToExisting: false,
                    fillMaxReturnQty,
                }),
            );
            return;
        }
        if (url.id || returnIdFromPath) {
            const targetReturnId = returnIdFromPath || url.id;
            // Skip fetch if return slip already in state with matching ID (e.g. after createReturnSlip)
            const alreadyLoaded = returnSlip?.id !== undefined && String(returnSlip.id) === String(targetReturnId);
            if (!alreadyLoaded) {
                dispatch(returnThunks.getReturnById({ returnId: targetReturnId }));
            }
            dispatch(configThunks.getReturnCenterSettings());
            return;
        }
        if (!state) {
            dispatch(resetOrderState());
            navigate('/');
            return;
        }
        // returnSlip.id and returnSlip.returnItems are intentionally excluded - they're used as guards
        // to prevent redundant fetches, not as triggers. Including them would cause infinite loops.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, orderIdFromPath, returnIdFromPath, url.id, state, navigate, isSearchOrderRoute]);

    useEffect(() => {
        if (
            !currentCompany ||
            settingsAvailable.isAvailable ||
            settingsAvailable.isLoading ||
            settingsAvailable.hasError
        ) {
            return;
        }

        dispatch(configThunks.getReturnCenterSettings());
    }, [dispatch, currentCompany, settingsAvailable]);

    // Load batch return data when batch items are detected
    useEffect(() => {
        if (hasBatchReturnItems) {
            dispatch(fetchBatchDestinations());
            dispatch(fetchOpenBatchReturns());
        }
    }, [dispatch, hasBatchReturnItems]);

    const toggleSearchBar = useCallback(() => {
        setIsModalOpen((prev) => {
            const next = !prev;
            if (next) {
                // Manual open defaults to order number search.
                setAdditionalOrderSearchMode('orderNumber');
            }
            return next;
        });
    }, []);

    const handleClearSearchString = useCallback(() => {
        dispatch(searchItemsStringSet(''));
        if (searchItemRef.current) {
            searchItemRef.current.value = '';
            searchItemRef.current.focus();
        }
    }, [dispatch]);

    // Navigation handlers
    const backToOrderList = useGoBackNavigation();
    const backToOrderSearch = useCallback(() => {
        dispatch(searchItemsStringSet(''));
        navigate('/');
    }, [dispatch, navigate]);

    // Helper to apply bulk reason change and evaluate decision codes
    const applyBulkReasonChange = useCallback(
        (reasonCode: number) => {
            if (!returnSlip) return;

            // Collect items for bulk update
            const itemsForDecisionCodeEval: Array<{
                orderNumber: string;
                sku: string;
                orderLineNumber: string;
                itemId: number | string;
                reasonCode: number;
                dispatchDate: string | null;
            }> = [];

            (returnSlip.returnItems ?? []).forEach((orderGroup) => {
                (orderGroup.orderLines ?? []).forEach((item) => {
                    if (item.type !== ReturnOrderLineType.Normal) return;
                    item.returnItems?.forEach((ri: ExtendedReturnItem) => {
                        const orderNumber = orderGroup.orderNumber ?? '';
                        const sku = item.sku ?? '';
                        const orderLineNumber = item.orderLineNumber ?? '';
                        const itemId = ri.id ?? ri.splitId ?? '';

                        // Update reason code
                        dispatch(
                            updateReturnItem({
                                orderNumber,
                                sku,
                                orderLineNumber,
                                itemId,
                                property: 'reasonCode',
                                value: reasonCode,
                                misPickReasonCodeIds,
                            }),
                        );

                        // Collect item for decision code evaluation if not manual
                        if (!ri.decisionCodeIsManual) {
                            itemsForDecisionCodeEval.push({
                                orderNumber,
                                sku,
                                orderLineNumber,
                                itemId,
                                reasonCode,
                                dispatchDate: item.dispatchDate ?? null,
                            });
                        }
                    });
                });
            });

            // Bulk evaluate decision codes for non-manual items
            if (hasDecisionCodes && itemsForDecisionCodeEval.length > 0) {
                dispatch(returnThunks.bulkEvaluateDecisionCodes({ items: itemsForDecisionCodeEval }));
            }
        },
        [dispatch, returnSlip, hasDecisionCodes, misPickReasonCodeIds],
    );

    // Handler for bulk reason change
    const handleBulkReasonChange = useCallback(
        (value: number) => {
            if (!dontAskAgainBulkAction) {
                // Show dialog and store the value
                setIsDialogOpen(true);
                setPendingReasonCode(value);
            } else {
                // Apply change directly
                applyBulkReasonChange(value);
            }
        },
        [dontAskAgainBulkAction, applyBulkReasonChange],
    );

    // Handler for confirming in dialog
    const handleConfirmBulkReason = useCallback(() => {
        if (!returnSlip || pendingReasonCode == null) return;
        applyBulkReasonChange(pendingReasonCode);
        if (bulkActionCheckbox) {
            dispatch(userThunks.setDialogPreference({ key: 'dontAskAgainBulkAction', value: true }));
        }
        setIsDialogOpen(false);
        setPendingReasonCode(null);
    }, [dispatch, returnSlip, pendingReasonCode, bulkActionCheckbox, applyBulkReasonChange]);

    // Handler for cancel
    const cancelBulkReasonChange = useCallback(() => {
        setIsDialogOpen(false);
        setPendingReasonCode(null);
        setBulkActionCheckbox(false);
    }, []);

    // Apply bulk max return quantities
    const applyBulkMaxQty = useCallback(() => {
        dispatch(setAllReturnItemsToMaxQty());
    }, [dispatch]);

    // QR actions / new-return handler
    const handleNewReturn = useGoHome();

    // Handler for bulk max qty button click
    const handleBulkMaxQtyClick = useCallback(() => {
        if (dontAskAgainBulkMaxQty) {
            applyBulkMaxQty();
        } else {
            setShowBulkMaxQtyDialog(true);
        }
    }, [dontAskAgainBulkMaxQty, applyBulkMaxQty]);

    // Handler for deleting return
    const handleDeleteReturn = useCallback(
        (e?: React.MouseEvent<HTMLButtonElement>) => {
            e?.stopPropagation();
            if (!returnSlipId) {
                return;
            }

            // For processing returns the ERP-credit warning must always be shown, so never
            // take the "don't ask again" fast-path.
            if (dontAskAgainDeleteReturn && !isProcessing) {
                dispatch(returnThunks.deleteReturnSlip(returnSlipId)).then(() => {
                    backToOrderList();
                });
            } else {
                setShowDeleteDialog(true);
            }
        },
        [dispatch, returnSlipId, backToOrderList, dontAskAgainDeleteReturn, isProcessing],
    );

    // Register page-specific handlers with Layout via Outlet context refs
    useEffect(() => {
        if (!outlet) return;
        outlet.incrementRef.current = isScanIncrementMode && searchMode === SCAN_MODE ? handleScanIncrement : undefined;
        outlet.setMaxQtyRef.current = applyBulkMaxQty;
        outlet.openModalRef.current = (mode) => {
            setAdditionalOrderSearchMode(mode);
            setIsModalOpen(true);
        };
        outlet.closeModalRef.current = () => {
            if (isModalOpen) {
                setIsModalOpen(false);
            }
        };
        outlet.openDeleteDialogRef.current = () => {
            setShowDeleteDialog(true);
            setDialogOpenedByScanner(true);
        };
        outlet.bulkReasonApplyRef.current = (value: number) => {
            const item = outlet.pendingSingleItemRef.current;
            if (item) {
                dispatch(updateReturnItem({ ...item, property: 'reasonCode', value, misPickReasonCodeIds }));
                blurActiveElement();
                outlet.pendingSingleItemRef.current = null;
            } else {
                applyBulkReasonChange(value);
            }
        };
        return () => {
            outlet.incrementRef.current = undefined;
            outlet.setMaxQtyRef.current = undefined;
            outlet.openModalRef.current = undefined;
            outlet.closeModalRef.current = undefined;
            outlet.openDeleteDialogRef.current = undefined;
            outlet.bulkReasonApplyRef.current = undefined;
        };
    }, [
        outlet,
        handleScanIncrement,
        applyBulkMaxQty,
        isScanIncrementMode,
        searchMode,
        applyBulkReasonChange,
        blurActiveElement,
        isModalOpen,
        setAdditionalOrderSearchMode,
        setIsModalOpen,
        misPickReasonCodeIds,
    ]);

    // Wire cancelDeleteDialogRef so cancelPending can close the delete dialog
    useEffect(() => {
        if (!outlet) return;
        outlet.cancelDeleteDialogRef.current = () => {
            setShowDeleteDialog(false);
            setDeleteReturnCheckbox(false);
            setDialogOpenedByScanner(false);
        };
        return () => {
            outlet.cancelDeleteDialogRef.current = undefined;
        };
    }, [outlet]);

    // Wire cancelNavigationRef so cancelPending can dismiss the nav-guard blocker
    useEffect(() => {
        if (!outlet) return;
        outlet.cancelNavigationRef.current = cancelNavigation;
        return () => {
            outlet.cancelNavigationRef.current = undefined;
        };
    }, [outlet, cancelNavigation]);

    // handleConfirmNavigation: called by both the dialog's confirm button and the
    // confirmNavigation scanner action. Proceeds the blocked navigation and clears Redux state.
    const handleConfirmNavigation = useCallback(() => {
        proceedNavigation();
        dispatch(clearReturnSlipAction());
        dispatch(clearSearchOrders());
    }, [proceedNavigation, dispatch]);

    // Wire confirmNavDialogRef so the scanner's confirmNavigation action can trigger the dialog confirm
    useEffect(() => {
        if (!outlet) return;
        outlet.confirmNavDialogRef.current = isNavBlocked ? handleConfirmNavigation : undefined;
        return () => {
            outlet.confirmNavDialogRef.current = undefined;
        };
    }, [outlet, isNavBlocked, handleConfirmNavigation]);

    // Auto-proceed when blocker fires but there's nothing unsaved
    // (e.g. after completeReturn or deleteReturn where state was already cleared)
    useEffect(() => {
        if (isNavBlocked && !hasUnsavedChanges) {
            proceedNavigation();
        }
    }, [isNavBlocked, hasUnsavedChanges, proceedNavigation]);

    // Handler for arrival date change - recalculate all non-manual decision codes
    const handleArrivalDateChange = useCallback(
        (date: Date | [Date, Date] | null) => {
            if (!date || date instanceof Array || !returnSlip) return;

            const utc = date.toISOString();
            dispatch(setArrivalDate(utc));

            // Collect all non-manual items for decision code re-evaluation
            if (hasDecisionCodes) {
                const itemsForDecisionCodeEval: Array<{
                    orderNumber: string;
                    sku: string;
                    orderLineNumber: string;
                    itemId: number | string;
                    reasonCode: number;
                    dispatchDate: string | null;
                }> = [];

                (returnSlip.returnItems ?? []).forEach((orderGroup) => {
                    (orderGroup.orderLines ?? []).forEach((item) => {
                        item.returnItems?.forEach((ri: ExtendedReturnItem) => {
                            if (!ri.decisionCodeIsManual && ri.reasonCode) {
                                itemsForDecisionCodeEval.push({
                                    orderNumber: orderGroup.orderNumber ?? '',
                                    sku: item.sku ?? '',
                                    orderLineNumber: item.orderLineNumber ?? '',
                                    itemId: ri.id ?? ri.splitId ?? '',
                                    reasonCode: ri.reasonCode,
                                    dispatchDate: item.dispatchDate ?? null,
                                });
                            }
                        });
                    });
                });

                if (itemsForDecisionCodeEval.length > 0) {
                    dispatch(returnThunks.bulkEvaluateDecisionCodes({ items: itemsForDecisionCodeEval }));
                }
            }
        },
        [dispatch, returnSlip, hasDecisionCodes],
    );

    // Handler for remove order click - shows confirmation dialog
    const handleRemoveOrderClick = useCallback((orderNumber: string) => {
        setPendingRemoveOrderNumber(orderNumber);
    }, []);

    // Handler for confirming order removal
    const handleConfirmRemoveOrder = useCallback(() => {
        if (!pendingRemoveOrderNumber) return;
        dispatch(removeOrderFromReturnSlip(pendingRemoveOrderNumber));
        setPendingRemoveOrderNumber(null);
    }, [dispatch, pendingRemoveOrderNumber]);

    const handleCancelRemoveOrder = useCallback(() => {
        setPendingRemoveOrderNumber(null);
    }, []);

    // Handler for confirming bulk max qty dialog
    const handleConfirmBulkMaxQty = useCallback(() => {
        applyBulkMaxQty();
        if (bulkMaxQtyCheckbox) {
            dispatch(userThunks.setDialogPreference({ key: 'dontAskAgainBulkMaxQty', value: true }));
        }
        setShowBulkMaxQtyDialog(false);
        setBulkMaxQtyCheckbox(false);
    }, [applyBulkMaxQty, bulkMaxQtyCheckbox, dispatch]);

    // Handler for canceling bulk max qty dialog
    const handleCancelBulkMaxQty = useCallback(() => {
        setShowBulkMaxQtyDialog(false);
        setBulkMaxQtyCheckbox(false);
    }, []);

    // Handler for confirming delete in dialog
    const handleConfirmDeleteReturn = useCallback(() => {
        if (!returnSlipId) return;

        dispatch(returnThunks.deleteReturnSlip(returnSlipId)).then(() => {
            backToOrderList();
        });

        if (deleteReturnCheckbox) {
            dispatch(userThunks.setDialogPreference({ key: 'dontAskAgainDeleteReturn', value: true }));
        }

        setShowDeleteDialog(false);
        setDeleteReturnCheckbox(false);
        setDialogOpenedByScanner(false);
    }, [dispatch, returnSlipId, deleteReturnCheckbox, backToOrderList]);

    // Register confirmDeleteDialogRef when delete dialog is open
    useEffect(() => {
        if (!outlet) return;
        outlet.confirmDeleteDialogRef.current = showDeleteDialog ? handleConfirmDeleteReturn : undefined;
    }, [outlet, showDeleteDialog, handleConfirmDeleteReturn]);

    const deleteCreditNotes = useMemo(() => {
        if (!isCompletedOrHasError) return [];
        return (returnSlip?.creditNotes ?? [])
            .map((cn) => cn.orderNumber)
            .filter((note): note is string => Boolean(note));
    }, [isCompletedOrHasError, returnSlip]);

    // Handler for canceling delete dialog
    const handleCancelDeleteReturn = useCallback(() => {
        setShowDeleteDialog(false);
        setDeleteReturnCheckbox(false);
        setDialogOpenedByScanner(false);
    }, []);

    // Render completed by user info
    const renderCompletedBy = () => {
        if (!isCompletedOrHasError) return null;
        return (
            <Flex column>
                <Text as="label" xBold fontSize={11} className="mb-1" padding={0} margin={0}>
                    {t('completedByUser', 'Completed by')}
                </Text>
                <CompletedByUserWrapper>
                    <Text fontSize={12} xBold>
                        {returnSlip?.completedByUser ?? 'Unknown'}
                    </Text>
                </CompletedByUserWrapper>
                {returnSlip?.completedWithoutCredit && (
                    <Text fontSize={12} className="mt-1">
                        {t('batchSending.completedWithoutCredit', 'Completed without credit')}
                    </Text>
                )}
            </Flex>
        );
    };

    // Render content (table)
    const renderContent = () => {
        const canRemoveOrder = !isCompletedOrHasError && (returnSlip?.returnItems ?? []).length > 1;
        return (
            <>
                {(returnSlip?.returnItems ?? []).map((orderGroup) => {
                    const orderId = orderGroup.orderNumber ?? '';
                    const sourceReturnId = orderGroup.sourceReturnId;
                    const groupKey = `${orderId}-${sourceReturnId ?? ''}`;
                    const items = orderGroup.orderLines ?? [];
                    const filtered = filteredReturnItems.find(
                        (group) => group.orderNumber === orderId && group.sourceReturnId === sourceReturnId,
                    );
                    const orderReturnSummaries = returnSlip?.returnSummaries?.[orderId] ?? [];
                    const rows = getRows(orderId, filtered, sourceReturnId);
                    return (
                        <TableWrapper $grow={1} column key={groupKey}>
                            <OrderItemCountDisplay
                                title={`${t('order')}: ${orderId}${
                                    sourceReturnId ? ` (${t('return')} #${sourceReturnId})` : ''
                                }`}
                                countLabel={t('items')}
                                items={items}
                                returnSummaries={orderReturnSummaries}
                                onRemove={() => handleRemoveOrderClick(orderId)}
                                canRemoveOrder={canRemoveOrder}
                            />
                            {items?.length ? (
                                <TableContainer gap="1rem" $grow={1}>
                                    <Flex column gap="1rem" $grow={1}>
                                        {rows.length > 0 && (
                                            <Padding>
                                                <Table
                                                    className={`${groupKey}-order-table f3-300 mb-3`}
                                                    bgColor="fillWhite"
                                                    cols={tableColumns.length}
                                                    headers={tableColumns}
                                                    rows={getRows(orderId, filtered, sourceReturnId)}
                                                    compact={compact}
                                                    renderCompactHeaders={compact}
                                                    highlightedKey={highlightedKey}
                                                    highlightVariant={
                                                        activeHighlightedRowKey?.startsWith(`${orderId}-`)
                                                            ? ACTIVE_MULTI_MATCH
                                                            : highlightVariant
                                                    }
                                                />
                                            </Padding>
                                        )}

                                        {!completesWithoutCredit && (
                                            <InvoiceChargesTable
                                                orderNumber={orderId}
                                                disabled={
                                                    isCompletedOrHasError ||
                                                    // Both settings exist to give Enterprise something to
                                                    // credit a charge against: a stand-in SKU, and an order
                                                    // type for a charge-only credit cart. M3 credits the
                                                    // charge itself and defaults the order type, so it needs
                                                    // neither.
                                                    (!isM3Company &&
                                                        (!settings?.invoiceChargeCreditSku ||
                                                            !settings?.invoiceChargeOrderType))
                                                }
                                                handleReturnItemChange={handleReturnItemChange}
                                                cellInputRefs={cellInputRefs}
                                                handleArrowKeyDown={(e, itemId, rowIndex, colIndex) =>
                                                    handleArrowKeyDown(
                                                        e,
                                                        itemId,
                                                        rowIndex,
                                                        colIndex,
                                                        `invoiceCharges-${orderId}`,
                                                    )
                                                }
                                            />
                                        )}
                                    </Flex>
                                </TableContainer>
                            ) : (
                                <NoResultsFound />
                            )}
                        </TableWrapper>
                    );
                })}
            </>
        );
    };

    // Error state
    if (hasError) {
        if (errorCode === 'orderTypeExcluded') {
            return (
                <Flex className={`${bp.width < 1300 ? 'p-3' : ''}`} gap="1rem" column $grow={1}>
                    <ErrorDisplay
                        errorMessage={t('orderTypeExcluded')}
                        backLabel={t('backToOrderSearch')}
                        title={t('orderTypeExcludedTitle')}
                        icon={faExclamationTriangle}
                    />
                </Flex>
            );
        }

        return (
            <Flex className={`${bp.width < 1300 ? 'p-3' : ''}`} gap="1rem" column $grow={1}>
                <ErrorDisplay errorMessage={errorMessage} title={t('error_occurred')} icon={faExclamationTriangle} />
            </Flex>
        );
    }

    // Loading state
    if (isLoading) {
        return (
            <Flex className={`${bp.width < 1300 ? 'p-3' : ''}`} gap="1rem" column $grow={1}>
                <SpinnerWrapper justify="center" align="start" $grow={1}>
                    <Spinner useFullHeight color="text" size="small" />
                </SpinnerWrapper>
            </Flex>
        );
    }

    return (
        <Flex className={`${bp.width < 1520 ? 'p-3' : 'pt-3'}`} gap="1rem" column $grow={1}>
            <GridWrapper columns={{ base: '1fr', md: '2fr 0.55fr', lg: '2fr 0.55fr' }} gap="1rem">
                <Flex column $grow={1}>
                    <OrderHeadDetails
                        title={isCompletedOrHasError ? t('details') : returnSlipId ? t('manageReturn') : t('newReturn')}
                        returnStatus={returnSlipStatus}
                        returnStatusTranslation={getStatsTranslations(t, returnSlipStatus ?? undefined)}
                        returnSlipId={returnSlipId}
                        orderNumbers={orderNumbers}
                        returnSlip={returnSlip}
                        isCompleted={isCompletedOrHasError}
                        handleArrivalDateChange={handleArrivalDateChange}
                        handleBulkReasonChange={handleBulkReasonChange}
                    />
                    <Flex column gap="0.5rem" className="order-table-wrapper" ref={orderListRef}>
                        {returnSlip ? (
                            <Container column $grow={1}>
                                <ButtonContainer className="w-100" $grow={1} align="center" gap="1rem">
                                    <Toggle>
                                        <ToggleBtn
                                            $active={searchMode === SCAN_MODE}
                                            onClick={() => setSearchMode(SCAN_MODE)}
                                            title={t('scanMode')}
                                        >
                                            <FontAwesomeIcon icon={faBarcode} size="xs" />
                                        </ToggleBtn>
                                        <ToggleBtn
                                            $active={searchMode === MANUAL_MODE}
                                            onClick={() => {
                                                setSearchMode(MANUAL_MODE);
                                                clearHighlight();
                                            }}
                                            title={t('manualMode')}
                                        >
                                            <FontAwesomeIcon icon={faMagnifyingGlass} size="xs" />
                                        </ToggleBtn>
                                    </Toggle>

                                    {searchMode === MANUAL_MODE ? (
                                        <Flex className="w-100" $grow={1}>
                                            <SearchInput
                                                inputRef={searchItemRef}
                                                containerRef={searchItemContainerRef}
                                                onSearch={(e) => dispatch(searchItemsStringSet(e.target.value))}
                                                onClear={handleClearSearchString}
                                                placeholder={t('searchItems')}
                                                searchString={searchQuery}
                                                height={2}
                                            />
                                        </Flex>
                                    ) : (
                                        <ScanMode $grow={1} align="center">
                                            <PulseDot />{' '}
                                            <Text className="ml-1" fontSize={14}>
                                                {t('readyToScan')}
                                            </Text>
                                        </ScanMode>
                                    )}

                                    <Flex gap="0.5rem">
                                        <SecondaryActionButton
                                            disabled={!buttonStates.canModify || hasSplitRows}
                                            onClick={handleBulkMaxQtyClick}
                                            title={
                                                hasSplitRows
                                                    ? t('cannotBulkMaxQtyWithSplits')
                                                    : t('setAllReturnQtyToMax')
                                            }
                                        >
                                            <FontAwesomeIcon icon={faAngleUp} size="xs" />
                                            <Text
                                                fontSize={14}
                                                color={
                                                    !buttonStates.canModify || hasSplitRows ? 'textDisabled' : 'text'
                                                }
                                            >
                                                {t('setMaxQty')}
                                            </Text>
                                        </SecondaryActionButton>

                                        <SecondaryActionButton
                                            disabled={!buttonStates.canModify}
                                            onClick={toggleSearchBar}
                                            title={t('addOrderTooltip')}
                                            backgroundColor="#D6E4F5"
                                        >
                                            <FontAwesomeIcon icon={faPlus} size="xs" color={LIGHT_BLUE} />
                                            <Text
                                                fontSize={14}
                                                color={buttonStates.canModify ? 'link' : 'textDisabled'}
                                                as={'p'}
                                            >
                                                {t('addOrder')}
                                            </Text>
                                        </SecondaryActionButton>

                                        {hasBatchReturnItems && (
                                            <SecondaryActionButton
                                                disabled={
                                                    !buttonStates.canModify &&
                                                    !buttonStates.isCreditedBatchPending &&
                                                    !buttonStates.isCreditError &&
                                                    !hasMovableBatchRows
                                                }
                                                onClick={() => setIsBatchModalOpen(true)}
                                                title={t('batchReturn.manageBatchReturns', 'Manage batch returns')}
                                                backgroundColor="#E8F5E9"
                                            >
                                                <FontAwesomeIcon icon={faBoxesStacked} size="xs" color="#4caf50" />
                                                {returnSlip?.batchHandled && (
                                                    <FontAwesomeIcon icon={faCheck} size="xs" color="#4caf50" />
                                                )}
                                                <Text
                                                    fontSize={14}
                                                    color={
                                                        buttonStates.canModify ||
                                                        buttonStates.isCreditedBatchPending ||
                                                        buttonStates.isCreditError
                                                            ? 'text'
                                                            : 'textDisabled'
                                                    }
                                                    as={'p'}
                                                >
                                                    {t('batchReturn.manageBatchReturns', 'Batch')}
                                                </Text>
                                            </SecondaryActionButton>
                                        )}
                                    </Flex>
                                </ButtonContainer>

                                {renderContent()}
                                {!completesWithoutCredit && (
                                    <>
                                        <IntercompanyPanel
                                            handleReturnItemChange={handleReturnItemChange}
                                            disabled={isCompletedOrHasError}
                                        />
                                        <ReturnAdditionalCosts
                                            disabled={isCompletedOrHasError}
                                            cellInputRefs={cellInputRefs}
                                            handleArrowKeyDown={(e, itemId, rowIndex, colIndex) =>
                                                handleArrowKeyDown(e, itemId, rowIndex, colIndex, 'additionalCosts')
                                            }
                                        />
                                    </>
                                )}
                            </Container>
                        ) : (
                            <ErrorDisplay
                                errorMessage={t('noReturnSlipFound')}
                                onBack={backToOrderList}
                                title={t('error_occurred')}
                                icon={faExclamationTriangle}
                            />
                        )}
                    </Flex>
                </Flex>
                <Container className="right-column-section" column gap="1rem">
                    <SideBar>
                        <ReturnActionPanel
                            isNewReturn={isNewReturn}
                            onSaveWithStatus={(status) => {
                                // Processing returns: warn about ERP credit before applying.
                                if (isProcessing) {
                                    setPendingProcessingStatus(status);
                                    return;
                                }
                                handleSaveWithStatus(status);
                                clearHighlight();
                            }}
                            onDelete={handleDeleteReturn}
                            onComplete={handleCompleteReturnClick}
                            onCompleteBatch={handleCompleteBatchClick}
                            onReopenBatchSlip={handleReopenBatchSlip}
                        />
                        {isCompletedOrHasError && (
                            <Flex column gap="0.5rem">
                                {renderCompletedBy()}
                                {/* Never credited: there is no credit note or e-commerce refund to show. */}
                                {!returnSlip?.completedWithoutCredit && (
                                    <>
                                        <CreditNotesCard />
                                        <B2CPanel />
                                    </>
                                )}
                            </Flex>
                        )}
                        <BatchSourceCard />
                        <ConnectedReturnsCard />
                        <OrderInfoCard />
                        {isModalOpen && (
                            <AdditionalOrderEditor
                                statusButtonLabel={statusButtonLabelWhenBlocked}
                                returnStatus={returnSlipStatus}
                                isModalOpen={isModalOpen}
                                setIsModalOpen={setIsModalOpen}
                                isReturnSlip={isNewReturn}
                                initialSearchMode={additionalOrderSearchMode}
                            />
                        )}
                        {/* All Confirmation Dialogs */}
                        <OrderDialogs
                            showCompleteDialog={showCompleteDialog}
                            completeReturnCheckbox={completeReturnCheckbox}
                            setCompleteReturnCheckbox={setCompleteReturnCheckbox}
                            handleConfirmCompleteReturn={handleConfirmCompleteReturn}
                            handleCancelCompleteReturn={handleCancelCompleteReturn}
                            showCompleteBatchDialog={showCompleteBatchDialog}
                            getUnassignedBatchItemCount={getUnassignedBatchItemCount}
                            handleConfirmCompleteBatch={handleConfirmCompleteBatch}
                            handleCancelCompleteBatch={handleCancelCompleteBatch}
                            showDeleteDialog={showDeleteDialog}
                            deleteReturnCheckbox={deleteReturnCheckbox}
                            setDeleteReturnCheckbox={setDeleteReturnCheckbox}
                            handleConfirmDeleteReturn={handleConfirmDeleteReturn}
                            handleCancelDeleteReturn={handleCancelDeleteReturn}
                            pendingProcessingStatus={pendingProcessingStatus}
                            setPendingProcessingStatus={setPendingProcessingStatus}
                            handleSaveWithStatus={handleSaveWithStatus}
                            clearHighlight={clearHighlight}
                            isDialogOpen={isDialogOpen}
                            setIsDialogOpen={setIsDialogOpen}
                            bulkActionCheckbox={bulkActionCheckbox}
                            setBulkActionCheckbox={setBulkActionCheckbox}
                            handleConfirmBulkReason={handleConfirmBulkReason}
                            cancelBulkReasonChange={cancelBulkReasonChange}
                            showBulkMaxQtyDialog={showBulkMaxQtyDialog}
                            bulkMaxQtyCheckbox={bulkMaxQtyCheckbox}
                            setBulkMaxQtyCheckbox={setBulkMaxQtyCheckbox}
                            handleConfirmBulkMaxQty={handleConfirmBulkMaxQty}
                            handleCancelBulkMaxQty={handleCancelBulkMaxQty}
                            pendingRemoveOrderNumber={pendingRemoveOrderNumber}
                            handleConfirmRemoveOrder={handleConfirmRemoveOrder}
                            handleCancelRemoveOrder={handleCancelRemoveOrder}
                            isNavBlocked={isNavBlocked}
                            hasUnsavedChanges={hasUnsavedChanges}
                            handleConfirmNavigation={handleConfirmNavigation}
                            cancelNavigation={cancelNavigation}
                            returnSlipId={returnSlipId}
                            isProcessing={isProcessing}
                            isCompletedOrHasError={isCompletedOrHasError}
                            deleteCreditNotes={deleteCreditNotes}
                            dialogOpenedByScanner={dialogOpenedByScanner}
                        />

                        <ReturnLogs />
                    </SideBar>
                </Container>
            </GridWrapper>

            <CommentsDrawer
                open={!!commentsTarget}
                onClose={() => setCommentsTarget(null)}
                target={commentsTarget}
                disabled={isCompletedOrHasError}
                handleReturnItemChange={handleReturnItemChange}
            />

            <BatchReturnDrawer
                open={!!batchDrawerTarget}
                onClose={() => setBatchDrawerTarget(null)}
                target={batchDrawerTarget}
                disabled={isCompletedOrHasError && !isCreditedBatchPending}
                handleReturnItemChange={handleReturnItemChange}
                batchDestinationNames={batchDestinationNames}
                openBatchReturns={openBatchReturns}
                moveMode={batchMoveMode}
                returnId={returnSlipId}
            />

            <BatchReturnModal
                open={isBatchModalOpen}
                onClose={() => setIsBatchModalOpen(false)}
                returnSlip={returnSlip}
                disabled={isCompletedOrHasError && !isCreditedBatchPending}
                handleReturnItemChange={handleReturnItemChange}
                batchDestinationNames={batchDestinationNames}
                openBatchReturns={openBatchReturns}
                onApprove={handleApproveBatchHandling}
                onUnapprove={handleUnapproveBatchHandling}
                moveMode={batchMoveMode}
            />
        </Flex>
    );
};

export default Order;

const GridWrapper = styled(Grid)`
    margin-top: 3rem;
`;

const SpinnerWrapper = styled(Flex)`
    margin-top: 3rem;
`;

const TableWrapper = styled(Flex)`
    margin-bottom: 1rem;
`;

const Container = styled(Flex)``;

const ButtonContainer = styled(Flex)`
    background-color: ${({ theme }) => theme.colors.fillWhite};
    padding: 1rem;
    border-radius: var(--radius);
    border-top-right-radius: 0px;
    border-top-left-radius: 0;
`;

const Toggle = styled(Flex)`
    border: 1px solid ${({ theme }) => theme.colors.borderLight};
    border-radius: var(--radius);
    overflow: hidden;
    background: ${({ theme }) => theme.colors.fillLight};
    flex-shrink: 0;
    padding: 3px;
    gap: 2px;
`;

const ToggleBtn = styled.button<{ $active: boolean }>`
    border-radius: var(--radius);
    background-color: ${({ theme, $active }) => ($active ? theme.colors.fillWhite : 'transparent')};
    box-shadow: ${({ $active }) => ($active ? '0 1px 3px #0066e2' : 'none')};
    width: 45px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: ${({ $active }) => ($active ? `1px solid #D6E4F5` : 'none')};
    color: ${({ theme, $active }) => ($active ? theme.colors.main : theme.colors.textLight)};
    cursor: pointer;
    transition: background 0.15s, color 0.15s, box-shadow 0.15s;
`;

const ScanMode = styled(Flex)`
    border-radius: var(--radius);
    border: 1px solid ${({ theme }) => theme.colors.borderLight};
    background-color: #f4f4f4;
    padding: 2px;

    & p {
        margin: 0.25rem;
    }
`;

const pulseAnim = keyframes`
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.25; }
`;

const PulseDot = styled.span`
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: #2b43e1e3;
    animation: ${pulseAnim} 1.3s ease-in-out infinite;
    margin-left: 0.75rem;
`;

const TableContainer = styled(TableWrapper)`
    background-color: ${({ theme }) => theme.colors.fillWhite};
    border-radius: var(--radius);
    padding: 0.5rem 0;
`;

const Padding = styled(Flex)`
    flex: 1;
    width: 100%;
    padding: 0 0.5rem;
    margin-top: 0.5rem;
`;

const CompletedByUserWrapper = styled(Flex)`
    background-color: ${({ theme }) => theme.colors.fillLight};
    padding: 0.25rem 1rem;
    border-radius: var(--radius);
`;
